// Copyright (c) Docugami, Inc. All rights reserved.
import { useState, useEffect } from "react"
import { Config } from '../config';
import { Fragment } from "../networking/api";
import { AAExpandCardletSchema, TelemetryEvent } from 'telemetry/TelemetryEvents';
import { Telemetry } from 'telemetry/TelemetryManager';
import { getLocalStorageAssistedAuthoringCardData, setLocalStorageAssistedAuthoringCardData } from "../utils/localStorageHelpers";

export enum ClauseCardType {
    MissingClause,
    VariantClause,
    ReviewClause
}

export interface IAaCardClauses {
    expandCard: (selectedFragment: number, fragmentSelection: (idx: number) => void, actionCallback: () => void) => void;
    collapsePopup: (doAction: boolean) => void;
    selectFragment: (fragmentIdx: number) => void;
    clauseData: () => IAaCardClausesData | null;
}

export interface IAaCardClausesData {
    id: string;
    cardType: ClauseCardType;
    header: string;
    text: string;
    fragments?: Fragment[];
}

enum ClauseDialogMessage {
    SelectFragment,
    CloseDialog
}

interface selectFragmentPayload {
    fragmentIdx: number;
}

interface closeDialogPayload {
    doAction: boolean;
}

interface clauseMessagePayload {
    action: ClauseDialogMessage,
    payload: selectFragmentPayload | closeDialogPayload
}

export function useAaCardClauses(data: IAaCardClausesData | null): IAaCardClauses {
    const [cardData, setCardData] = useState(data);

    useEffect(() => {
        if (!data) {
            let storedData = getLocalStorageAssistedAuthoringCardData();
            if (!!storedData) {
                setCardData(storedData);
            }
        }
    }, [data])

    function expandCard(
        selectedFragment: number,
        fragmentSelection: (idx: number) => void,
        actionCallback: () => void): void {
        Telemetry.trackEvent<AAExpandCardletSchema>({
            eventName: TelemetryEvent.AAExpandCardlet
        });
        setLocalStorageAssistedAuthoringCardData(cardData);

        const Office = (window as any).Office;
        let url = `${Config.baseUrl}/aaClauses/selectedClause/${selectedFragment}`;
        Office.context.ui.displayDialogAsync(
            url,
            { height: 50, width: 50, displayInIframe: true },
            function (asyncResult: any) {
                var dialog = asyncResult.value;
                dialog.addEventHandler(Office.EventType.DialogMessageReceived, (message: any) => {
                    let msg: clauseMessagePayload = JSON.parse(message.message);

                    switch (msg.action) {
                        case ClauseDialogMessage.SelectFragment:
                            {
                                let payload: selectFragmentPayload = msg.payload as selectFragmentPayload;
                                fragmentSelection(payload.fragmentIdx);
                                break;
                            }

                        case ClauseDialogMessage.CloseDialog:
                            {
                                let payload: closeDialogPayload = msg.payload as closeDialogPayload;
                                if (payload.doAction) {
                                    actionCallback();
                                }
                                dialog.close();
                                break;
                            }

                        default:
                            console.error(`unknown message payload from Clauses Dialog: ${message}`);
                    }
                });
            })
    }

    function collapsePopup(doAction: boolean): void {
        const Office = (window as any).Office;
        Office.context.ui.messageParent(JSON.stringify({
            action: ClauseDialogMessage.CloseDialog,
            payload: { doAction: doAction }
        }));
    }

    function selectFragment(fragmentIdx: number): void {
        const Office = (window as any).Office;
        Office.context.ui.messageParent(JSON.stringify({
            action: ClauseDialogMessage.SelectFragment,
            payload: { fragmentIdx: fragmentIdx }
        }));
    }

    function clauseData(): IAaCardClausesData | null {
        return cardData;
    }

    return {
        expandCard: expandCard,
        collapsePopup: collapsePopup,
        selectFragment: selectFragment,
        clauseData: clauseData
    }
}
